import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from '@tiptap/react';
import { BookOpen, Link2, Sparkles, X } from 'lucide-react';
import {
  getCharacterCueDialogueDecorations,
  getDialogueStatsInRange,
  type SectionDialogueStats,
} from '@narrativebench/editor-core';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './ui/dropdown-menu';
import { TooltipProvider, ButtonTooltip } from './ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import {
  emptyHeadingSectionReferences,
  useHeadingSectionMenu,
  type HeadingSectionReferences,
  type ManualChapterMenuOption,
} from '../contexts/HeadingSectionMenuContext';
import { getSectionRangeForHeading } from '../utils/document-section';
import { getDocumentPlainText } from '../utils/document-plain-text';
import {
  getFeedbackAnalyzeDisableTooltip,
  getSectionAnalyzeDisableReason,
} from '../utils/feedback-analyze-availability';
import {
  formatTextLengthStatLabels,
  getTextLengthStats,
  type TextLengthStats,
} from '../utils/word-count';
import { formatSceneDialogueStatLabels } from '../utils/script-scene-stats';
import { getDocumentPanelPath } from '../utils/document-panel-paths';
import { copyTextToClipboard } from '../utils/copy-text-to-clipboard';
import {
  ensureDocumentSpeechLanguage,
  speakDocumentText,
} from '../utils/document-speech';
import { toBcp47Locale } from '../locales/locale';
import { trackFeatureUsage } from '../utils/track-feature-usage';
import { useBlockSectionScopeHighlight } from '../utils/block-section-scope-highlight';
import { preserveScrollDuring } from '../utils/editor-utils';
import { RelatedEntityLink } from './RelatedEntityLink';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import {
  BlockMenuAnalyzeItem,
  BlockMenuChatItem,
  BlockMenuCopyItem,
  BlockMenuReadAloudItem,
  BlockMenuScriptInfoSection,
  BlockMenuStatsSection,
} from './block-section-menu/BlockSectionMenuItems';
import { BlockSectionMenuShell } from './block-section-menu/BlockSectionMenuShell';
import { ScriptInfoIconWithTooltip } from './script-info/ScriptInfoIconWithTooltip';
import type { ScriptInfoStatsLabels } from './script-info/ScriptInfoStats';

function HeadingNodeViewContent({
  level,
  className,
}: {
  level: number;
  className: string;
}): React.ReactElement {
  switch (level) {
    case 2:
      return <NodeViewContent<'h2'> as="h2" className={className} />;
    case 3:
      return <NodeViewContent<'h3'> as="h3" className={className} />;
    case 4:
      return <NodeViewContent<'h4'> as="h4" className={className} />;
    case 5:
      return <NodeViewContent<'h5'> as="h5" className={className} />;
    case 6:
      return <NodeViewContent<'h6'> as="h6" className={className} />;
    case 1:
    default:
      return <NodeViewContent<'h1'> as="h1" className={className} />;
  }
}

function formatManualChapterLabel(chapter: ManualChapterMenuOption): string {
  return chapter.order != null ? `${chapter.order} ${chapter.title}` : chapter.title;
}

function chapterPanelHref(
  projectId: string,
  documentId: string,
  panel: 'chapters' | 'analysis',
  chapterId: string,
): string {
  return `${getDocumentPanelPath(projectId, documentId, panel)}?chapterId=${encodeURIComponent(chapterId)}`;
}

export const HeadingWithSectionMenuView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
}) => {
  const { t, locale } = useLocale();
  const { token, masterKey } = useAuth();
  const { showToast } = useToast();
  const { writingSettings } = useSettings();
  const menu = useHeadingSectionMenu();
  const level = Math.min(Math.max(Number(node.attrs.level) || 1, 1), 6);
  const showMenu = Boolean(menu) && editor.isEditable;
  const [disableReason, setDisableReason] = useState<string | undefined>();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sectionStats, setSectionStats] = useState<TextLengthStats | null>(null);
  const [sectionReferences, setSectionReferences] = useState<HeadingSectionReferences>(
    emptyHeadingSectionReferences(),
  );
  const [linkedChapterId, setLinkedChapterId] = useState<string | null>(null);
  const [hasUserLink, setHasUserLink] = useState(false);
  const [linkingChapter, setLinkingChapter] = useState(false);
  const [hoveringSection, setHoveringSection] = useState(false);
  const [scopeRange, setScopeRange] = useState<{ from: number; to: number } | null>(null);
  const [sectionDialogueStats, setSectionDialogueStats] = useState<SectionDialogueStats | null>(null);

  useBlockSectionScopeHighlight(editor, menuOpen || hoveringSection ? scopeRange : null);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !writingSettings.enableScriptMode) {
      setSectionDialogueStats(null);
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const refreshScriptStats = (): void => {
      const hintPos = getPos();
      if (typeof hintPos !== 'number') {
        setSectionDialogueStats(null);
        return;
      }
      const range = getSectionRangeForHeading(
        editor.state.doc,
        hintPos,
        level,
        node.textContent,
      );
      if (!range) {
        setSectionDialogueStats(null);
        return;
      }
      const decorations = getCharacterCueDialogueDecorations(editor.state);
      setSectionDialogueStats(
        getDialogueStatsInRange(editor.state.doc, range.from, range.to, decorations),
      );
    };

    const scheduleRefreshScriptStats = (): void => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        timeoutId = null;
        refreshScriptStats();
      }, 250);
    };

    refreshScriptStats();
    editor.on('transaction', scheduleRefreshScriptStats);
    return () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
      editor.off('transaction', scheduleRefreshScriptStats);
    };
  }, [editor, getPos, level, node.textContent, writingSettings.enableScriptMode]);

  const refreshSectionAvailability = (): { from: number; to: number; stats: TextLengthStats; text: string } | null => {
    const hintPos = getPos();
    if (typeof hintPos !== 'number') {
      setDisableReason(undefined);
      setSectionStats(null);
      setSectionReferences(emptyHeadingSectionReferences());
      setLinkedChapterId(null);
      setHasUserLink(false);
      setScopeRange(null);
      return null;
    }
    const range = getSectionRangeForHeading(
      editor.state.doc,
      hintPos,
      level,
      node.textContent,
    );
    if (!range) {
      setDisableReason(undefined);
      setSectionStats(null);
      setSectionReferences(emptyHeadingSectionReferences());
      setLinkedChapterId(null);
      setHasUserLink(false);
      setScopeRange(null);
      return null;
    }
    const text = getDocumentPlainText(editor.state.doc, range.from, range.to);
    const stats = getTextLengthStats(text);
    setSectionStats(stats);
    setScopeRange(range);
    setSectionReferences(
      menu?.getChapterReferencesForHeading(node.textContent) ?? emptyHeadingSectionReferences(),
    );
    setLinkedChapterId(menu?.getEffectiveManualChapterIdForHeading(node.textContent) ?? null);
    setHasUserLink(menu?.hasUserLinkedManualChapter(node.textContent) ?? false);
    const reason = getSectionAnalyzeDisableReason(stats.wordCount);
    setDisableReason(getFeedbackAnalyzeDisableTooltip(reason, t));
    return { ...range, stats, text };
  };

  const handleAnalyze = (): void => {
    if (!menu || menu.isAnalyzing) {
      return;
    }
    const range = refreshSectionAvailability();
    if (!range) {
      return;
    }
    if (getSectionAnalyzeDisableReason(range.stats.wordCount) !== null) {
      return;
    }
    setMenuOpen(false);
    menu.onAnalyzeSection(range.from, range.to);
  };

  const handleChatAboutSection = (): void => {
    if (!menu) {
      return;
    }
    const range = refreshSectionAvailability();
    if (!range) {
      return;
    }
    setMenuOpen(false);
    menu.onChatAboutSection(range.from, range.to);
  };

  const handleCopy = (): void => {
    const range = refreshSectionAvailability();
    if (!range) {
      return;
    }
    setMenuOpen(false);
    void copyTextToClipboard(range.text).then((copied) => {
      if (copied) {
        showToast(t('copied_to_clipboard'));
      }
    });
  };

  const dismissMenuWithoutScrolling = (afterDismiss?: () => void): void => {
    preserveScrollDuring(editor, () => {
      flushSync(() => {
        setMenuOpen(false);
      });
      afterDismiss?.();
      if (!editor.isDestroyed) {
        editor.commands.focus(undefined, { scrollIntoView: false });
      }
    });
  };

  const handleReadAloud = (): void => {
    const range = refreshSectionAvailability();
    if (!range || !range.text.trim()) {
      dismissMenuWithoutScrolling();
      return;
    }
    dismissMenuWithoutScrolling(() => {
      ensureDocumentSpeechLanguage(toBcp47Locale(locale));
      const started = speakDocumentText(range.text, {
        sourceRange: { from: range.from, to: range.to },
      });
      if (started) {
        void trackFeatureUsage({
          feature: 'document_read_aloud',
          documentId: menu?.documentId,
          token,
          masterKey,
        });
      }
    });
  };

  const handleLinkChapter = async (manualChapterId: string): Promise<void> => {
    if (!menu || linkingChapter) {
      return;
    }
    setLinkingChapter(true);
    const linked = await menu.linkHeadingToManualChapter(node.textContent, manualChapterId);
    setLinkingChapter(false);
    if (!linked) {
      return;
    }
    setLinkedChapterId(manualChapterId);
    setHasUserLink(true);
    setSectionReferences(menu.getChapterReferencesForHeading(node.textContent));
    setMenuOpen(false);
  };

  const handleUnlinkChapter = async (): Promise<void> => {
    if (!menu || linkingChapter) {
      return;
    }
    setLinkingChapter(true);
    const unlinked = await menu.unlinkHeadingFromManualChapter(node.textContent);
    setLinkingChapter(false);
    if (!unlinked) {
      return;
    }
    setHasUserLink(false);
    setLinkedChapterId(menu.getEffectiveManualChapterIdForHeading(node.textContent));
    setSectionReferences(menu.getChapterReferencesForHeading(node.textContent));
    setMenuOpen(false);
  };

  const sectionStatLabels = sectionStats ? formatTextLengthStatLabels(sectionStats, t) : null;
  const hasReferences =
    sectionReferences.linksTo.length > 0 || sectionReferences.referencedIn.length > 0;
  const projectId = menu?.projectId ?? null;
  const documentId = menu?.documentId ?? null;
  const manualChapters = menu?.manualChapters ?? [];
  const canLinkChapters = manualChapters.length > 0;
  const hasEntitiesSection = hasReferences || canLinkChapters;
  const hasStatsSection = Boolean(sectionStatLabels);
  const linkedChapter = linkedChapterId
    ? manualChapters.find((chapter) => chapter.id === linkedChapterId) ?? null
    : null;
  const chapterPickerLabel = linkedChapter
    ? formatManualChapterLabel(linkedChapter)
    : linkedChapterId
      ? t('change_chapter_link')
      : t('link_chapter');
  const outlineHref =
    linkedChapterId && projectId && documentId
      ? chapterPanelHref(projectId, documentId, 'chapters', linkedChapterId)
      : null;
  const analysisHref =
    linkedChapterId && projectId && documentId
      ? chapterPanelHref(projectId, documentId, 'analysis', linkedChapterId)
      : null;

  const chapterPicker = canLinkChapters ? (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2 px-2 py-1.5 text-xs leading-5 text-muted-foreground focus:text-muted-foreground data-[state=open]:text-muted-foreground">
        {linkedChapterId ? (
          <BookOpen className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <Link2 className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate">{chapterPickerLabel}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="z-[2200] max-h-64 min-w-[14rem] overflow-y-auto p-1">
        {manualChapters.map((chapter) => {
          const isLinked = chapter.id === linkedChapterId;
          const showUnlink = isLinked && hasUserLink;
          return (
            <div
              key={chapter.id}
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
                linkingChapter || isLinked
                  ? 'cursor-default text-muted-foreground'
                  : 'cursor-pointer hover:bg-accent hover:text-accent-foreground',
              )}
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                if (linkingChapter || isLinked) {
                  return;
                }
                void handleLinkChapter(chapter.id);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                  return;
                }
                event.preventDefault();
                if (linkingChapter || isLinked) {
                  return;
                }
                void handleLinkChapter(chapter.id);
              }}
            >
              <BookOpen className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{formatManualChapterLabel(chapter)}</span>
              {showUnlink ? (
                <TooltipProvider delayDuration={200}>
                  <ButtonTooltip
                    tooltip={{
                      children: t('unlink_chapter_tooltip'),
                      className: 'z-[2300] bg-popover text-popover-foreground border border-border shadow-md',
                    }}
                    side="right"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 shrink-0 rounded-full text-foreground opacity-70 hover:opacity-100"
                      disabled={linkingChapter}
                      aria-label={t('unlink_chapter')}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        void handleUnlinkChapter();
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </ButtonTooltip>
                </TooltipProvider>
              ) : null}
            </div>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  ) : null;

  const scriptInfoLabels: ScriptInfoStatsLabels | null = sectionDialogueStats
    ? formatSceneDialogueStatLabels(sectionDialogueStats, t)
    : null;

  return (
    <NodeViewWrapper
      className={cn(
        'heading-with-section-menu',
        `heading-with-section-menu-h${level}`,
        scriptInfoLabels ? 'has-script-info' : null,
      )}
      data-level={level}
    >
      {scriptInfoLabels ? (
        <ScriptInfoIconWithTooltip
          className="heading-script-info"
          labels={scriptInfoLabels}
        />
      ) : null}
      {showMenu && (
        <div
          onMouseEnter={() => {
            setHoveringSection(true);
            refreshSectionAvailability();
          }}
          onMouseLeave={() => {
            setHoveringSection(false);
          }}
        >
          <BlockSectionMenuShell
            menuOpen={menuOpen}
            onMenuOpenChange={(open) => {
              setMenuOpen(open);
              if (open) {
                setHoveringSection(true);
                refreshSectionAvailability();
              }
            }}
            ariaLabel={t('heading_section_menu')}
          >
          <BlockMenuAnalyzeItem
            isAnalyzing={Boolean(menu?.isAnalyzing)}
            disableReason={disableReason}
            onAnalyze={handleAnalyze}
          />
          <BlockMenuChatItem onChat={handleChatAboutSection} />
          <BlockMenuReadAloudItem onReadAloud={handleReadAloud} />
          <BlockMenuCopyItem onCopy={handleCopy} />
          {hasEntitiesSection ? (
            <>
              <DropdownMenuSeparator />
              {hasReferences && projectId ? (
                <TooltipProvider>
                  <div
                    className="px-2 py-1.5"
                    onClick={() => {
                      setMenuOpen(false);
                    }}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {sectionReferences.linksTo.map((entity) => (
                        <RelatedEntityLink
                          key={`links-${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                          entity={entity}
                          projectId={projectId}
                          className="text-xs"
                        />
                      ))}
                      {sectionReferences.referencedIn.map((entity) => (
                        <RelatedEntityLink
                          key={`refs-${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                          entity={entity}
                          projectId={projectId}
                          className="text-xs"
                        />
                      ))}
                    </div>
                  </div>
                </TooltipProvider>
              ) : null}
              {linkedChapterId && hasReferences ? <DropdownMenuSeparator /> : null}
              {chapterPicker}
              {outlineHref ? (
                <DropdownMenuItem asChild className="gap-2">
                  <Link to={outlineHref} onClick={() => setMenuOpen(false)}>
                    <BookOpen className="h-4 w-4" />
                    {t('go_to_chapter_outline')}
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {analysisHref ? (
                <DropdownMenuItem asChild className="gap-2">
                  <Link to={analysisHref} onClick={() => setMenuOpen(false)}>
                    <Sparkles className="h-4 w-4" />
                    {t('go_to_chapter_analysis')}
                  </Link>
                </DropdownMenuItem>
              ) : null}
            </>
          ) : null}
          {hasStatsSection ? <BlockMenuStatsSection labels={sectionStatLabels!} /> : null}
          {scriptInfoLabels ? <BlockMenuScriptInfoSection labels={scriptInfoLabels} /> : null}
        </BlockSectionMenuShell>
        </div>
      )}
      <HeadingNodeViewContent level={level} className="heading-with-section-menu-content" />
    </NodeViewWrapper>
  );
};
